const DEFAULT_LOCALE = 'en'
const SUPPORTED_LOCALES = ['en', 'fr', 'es', 'de']
const LOCALE_STORAGE_KEY = 'radiate.preAuthLocale'

let fallbackMessagesPromise = null

function normalizeLocale(locale) {
  if (!locale || typeof locale !== 'string') {
    return null
  }

  const normalizedLocale = locale.toLowerCase().split('-')[0]
  return SUPPORTED_LOCALES.includes(normalizedLocale) ? normalizedLocale : null
}

function getNestedValue(obj, keyPath) {
  return keyPath.split('.').reduce((acc, key) => {
    if (acc && typeof acc === 'object' && key in acc) {
      return acc[key]
    }
    return undefined
  }, obj)
}

function interpolateValue(value, vars = {}) {
  return value.replace(/\{(\w+)\}/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
  })
}

function parseVars(rawVars) {
  if (!rawVars) {
    return {}
  }

  try {
    return JSON.parse(rawVars)
  } catch (error) {
    console.warn('Failed to parse i18n vars:', error)
    return {}
  }
}

function getBrowserDefaultLocale() {
  return normalizeLocale(window.navigator.language)
}

function resolveLocale() {
  const searchParams = new URLSearchParams(window.location.search)
  const queryLocale = normalizeLocale(searchParams.get('lang'))
  if (queryLocale) {
    return { locale: queryLocale, source: 'query' }
  }

  try {
    const storedLocale = normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY))
    if (storedLocale) {
      return { locale: storedLocale, source: 'storage' }
    }
  } catch (error) {
    console.warn('Failed to read stored locale:', error)
  }

  const browserLocales = Array.isArray(window.navigator.languages)
    ? window.navigator.languages
    : [window.navigator.language]

  for (const browserLocale of browserLocales) {
    const normalizedLocale = normalizeLocale(browserLocale)
    if (normalizedLocale) {
      return { locale: normalizedLocale, source: 'browser' }
    }
  }

  return { locale: DEFAULT_LOCALE, source: 'default' }
}

async function fetchMessages(locale) {
  const response = await fetch(`/locales/pre-auth/${locale}.json`, {
    credentials: 'same-origin',
    cache: 'no-cache',
  })

  if (!response.ok) {
    throw new Error(`Unable to load pre-auth locale: ${locale}`)
  }

  return response.json()
}

export function buildLocalizedPath(pathname, locale, params = {}, includeExplicitLang = true) {
  const url = new URL(pathname, window.location.origin)

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }
    url.searchParams.set(key, value)
  })

  // Only include lang param if:
  // 1. It's not the default (en), AND
  // 2. It was explicitly chosen (via URL param or localStorage), not auto-detected from browser
  if (locale && locale !== DEFAULT_LOCALE && includeExplicitLang) {
    url.searchParams.set('lang', locale)
  }

  return `${url.pathname}${url.search}`
}

export function buildPreAuthLangHeader(locale) {
  const normalizedLocale = normalizeLocale(locale)
  if (!normalizedLocale || normalizedLocale === DEFAULT_LOCALE) {
    return {}
  }

  return { lang: normalizedLocale }
}

export async function initPreAuthPageI18n() {
  const { locale, source } = resolveLocale()
  
  const localeMessagesPromise = fetchMessages(locale).catch((error) => {
    console.warn(error)
    return locale === DEFAULT_LOCALE ? {} : null
  })

  if (!fallbackMessagesPromise) {
    fallbackMessagesPromise = fetchMessages(DEFAULT_LOCALE).catch((error) => {
      console.warn(error)
      return {}
    })
  }

  const [localeMessages, fallbackMessages] = await Promise.all([
    localeMessagesPromise,
    fallbackMessagesPromise,
  ])

  const activeMessages = localeMessages || fallbackMessages || {}

  const t = (key, vars = {}) => {
    const value = getNestedValue(activeMessages, key) ?? getNestedValue(fallbackMessages, key)

    if (typeof value !== 'string') {
      return key
    }

    return interpolateValue(value, vars)
  }

  const applyTranslations = (root = document) => {
    root.querySelectorAll('[data-i18n]').forEach((node) => {
      node.textContent = t(node.dataset.i18n, parseVars(node.dataset.i18nVars))
    })

    root.querySelectorAll('[data-i18n-html]').forEach((node) => {
      node.innerHTML = t(node.dataset.i18nHtml, parseVars(node.dataset.i18nVars))
    })

    root.querySelectorAll('[data-i18n-placeholder]').forEach((node) => {
      node.setAttribute(
        'placeholder',
        t(node.dataset.i18nPlaceholder, parseVars(node.dataset.i18nVars))
      )
    })

    root.querySelectorAll('[data-i18n-title]').forEach((node) => {
      node.setAttribute('title', t(node.dataset.i18nTitle, parseVars(node.dataset.i18nVars)))
    })

    root.querySelectorAll('[data-i18n-aria-label]').forEach((node) => {
      node.setAttribute(
        'aria-label',
        t(node.dataset.i18nAriaLabel, parseVars(node.dataset.i18nVars))
      )
    })
  }

  document.documentElement.lang = locale

  try {
    // For explicit lang query params, clear storage when it matches browser default.
    // Otherwise, persist explicit overrides so they carry across pages.
    if (source === 'query') {
      const browserDefaultLocale = getBrowserDefaultLocale()
      if (browserDefaultLocale && locale === browserDefaultLocale) {
        window.localStorage.removeItem(LOCALE_STORAGE_KEY)
      } else {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
      }
    }
    // For storage/browser/default resolution, keep existing storage untouched.
  } catch (error) {
    console.warn('Failed to persist locale:', error)
  }

  return {
    locale,
    t,
    applyTranslations,
    buildLangHeader: () => buildPreAuthLangHeader(locale),
    buildLocalizedPath: (pathname, params = {}) =>
      buildLocalizedPath(pathname, locale, params, source === 'query' || source === 'storage'),
  }
}